import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useApi } from "../../../containers/ApiContainer";
import { useIdentity } from "../../../containers/IdentityContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { fetchAircraft } from "../../../hooks/api";
import { useTenants } from "../../../hooks/useTenants";
import { Aircraft, Stack as StackType, Tenant } from "../../../types";
import {
  consistentId,
  defaultPreferenceForAircraft,
  uuidv4,
} from "../../../utils";
import { uniqBy } from "../../../utils/lodash-replacements";
import { AutocompleteAircraft } from "./AutocompleteAircraft";

type BulkAddByTailNumberProps = {
  stack: StackType | null;
  bulkTenantsToAdd: Tenant[];
  setBulkTenantsToAdd: React.Dispatch<React.SetStateAction<Tenant[]>>;
};

const BulkAddByTailNumber: React.FC<BulkAddByTailNumberProps> = ({
  stack,
  bulkTenantsToAdd,
  setBulkTenantsToAdd,
}) => {
  const { airplxIdentity } = useIdentity();
  const { activeFBO } = useActiveFBOs();
  const { postgrest } = useApi();
  const { tenants } = useTenants();
  const [tailNumbers, setTailNumbers] = React.useState<string[]>([]);
  const [unfoundTailNumbers, setUnfoundTailNumbers] = React.useState<string[]>(
    []
  );
  const [manualAircraftSelections, setManualAircraftSelections] =
    React.useState<Record<string, Aircraft>>({});
  const [hasSearched, setHasSearched] = React.useState<boolean>(false);
  const [hasSubmitted, setHasSubmitted] = React.useState<boolean>(false);

  // Clear manual selections when tail numbers change
  React.useEffect(() => {
    setManualAircraftSelections((prev) => {
      const filtered: Record<string, Aircraft> = {};
      tailNumbers.forEach((tn) => {
        if (prev[tn]) {
          filtered[tn] = prev[tn];
        }
      });
      return filtered;
    });
    setHasSearched(false); // Reset search state when tail numbers change
    setHasSubmitted(false); // Reset submission state when tail numbers change
  }, [tailNumbers]);

  const fetchTenantsByTailNumbers = React.useCallback(async () => {
    if (tailNumbers.length === 0) {
      setUnfoundTailNumbers([]);
      return [];
    }
    const existingTenants = tenants
      .filter((t) => tailNumbers.includes(t.tail_number))
      .map((t) => {
        const entity_id = t.entity_id || uuidv4();
        return {
          ...t,
          position: {
            ...(t.position ?? {}),
            id: t.position?.id || consistentId(entity_id, false),
            stack_id: stack?.id ?? null,
            entity_id: entity_id,
            x: null,
            y: stack ? -1 * Math.max(t.aircraft.length, t.aircraft.wingspan) : null,
            angle: null,
            preferences: defaultPreferenceForAircraft(t.aircraft, activeFBO),
          },
        };
      });
    const newTenantTailNumbers = tailNumbers.filter(
      (tn) => !existingTenants.some((t) => t.tail_number === tn)
    );

    const { data, error } = await postgrest
      .from("vw_faa_tail_numbers")
      .select()
      .in("tail_number", newTenantTailNumbers);

    if (error) {
      throw new Error(error.message);
    }

    // Identify tail numbers not found in the database
    const foundTailNumbers = data.map((row) => row.tail_number);
    const unfoundTailNumbers = newTenantTailNumbers.filter(
      (tn) => !foundTailNumbers.includes(tn)
    );
    setUnfoundTailNumbers(unfoundTailNumbers);

    const doit = data.map(async ({ tail_number, aircraft_id }) => {
      const aircraft = await fetchAircraft(postgrest, aircraft_id, activeFBO);
      return {
        tail_number,
        aircraft_id,
        aircraft: aircraft,
      };
    });
    const newTenantInfos = await Promise.all(doit);

    const newTenants = newTenantInfos.map((newTenantInfo, i) => {
      const entity_id = uuidv4();
      const tenant: Tenant = {
        id: uuidv4(),
        placement_id: uuidv4(),
        fbo_id: activeFBO.id,
        entity_id,
        tail_number: newTenantInfo.tail_number || "",
        owner_manager: "",
        note: "",
        note_last_updated: new Date().toISOString(),
        note_last_updated_by: airplxIdentity?.email ?? "Unknown",
        type: stack?.isReference ? "base" : "transient",
        aircraft_id: newTenantInfo.aircraft.id,
        aircraft: newTenantInfo.aircraft,
        selected: false,
        position: {
          id: consistentId(entity_id, false),
          stack_id: stack?.id ?? null,
          entity_id,
          x: null,
          y: stack
            ? -1 *
              Math.max(
                newTenantInfo.aircraft.length,
                newTenantInfo.aircraft.wingspan
              )
            : null,
          angle: null,
          preferences: defaultPreferenceForAircraft(
            newTenantInfo.aircraft,
            activeFBO
          ),
        },
      };
      return tenant;
    });

    return [...existingTenants, ...newTenants];
  }, [postgrest, tailNumbers, tenants, stack, activeFBO, airplxIdentity]);

  const handleSubmit = async () => {
    if (!hasSearched) {
      // First time - just search and identify unfound tail numbers
      setHasSearched(true);
      await fetchTenantsByTailNumbers(); // This will set unfoundTailNumbers
    } else {
      // Second time - actually add the tenants
      const newTenants = await fetchTenantsByTailNumbers();

      // Create tenants for manually specified aircraft
      const manualTenants = unfoundTailNumbers
        .filter((tailNumber) => manualAircraftSelections[tailNumber])
        .map((tailNumber) => {
          const aircraft = manualAircraftSelections[tailNumber];
          const entity_id = uuidv4();
          const tenant: Tenant = {
            id: uuidv4(),
            placement_id: uuidv4(),
            fbo_id: activeFBO.id,
            entity_id,
            tail_number: tailNumber,
            owner_manager: "",
            note: "",
            note_last_updated: new Date().toISOString(),
            note_last_updated_by: airplxIdentity?.email ?? "Unknown",
            type: stack?.isReference ? "base" : "transient",
            aircraft_id: aircraft.id,
            aircraft: aircraft,
            selected: false,
            position: {
              id: consistentId(entity_id, false),
              stack_id: stack?.id ?? null,
              entity_id,
              x: null,
              y: stack ? -1 * Math.max(aircraft.length, aircraft.wingspan) : null,
              angle: null,
              preferences: defaultPreferenceForAircraft(aircraft, activeFBO),
            },
          };
          return tenant;
        });

      const newTenantsToAdd = uniqBy(
        [...bulkTenantsToAdd, ...newTenants, ...manualTenants],
        "id"
      );
      setBulkTenantsToAdd(newTenantsToAdd);
      setHasSubmitted(true); // Mark as submitted to hide the correction panel
    }
  };

  const removeUnfoundTailNumber = (tailNumberToRemove: string) => {
    setUnfoundTailNumbers((prev) =>
      prev.filter((tn) => tn !== tailNumberToRemove)
    );
    setManualAircraftSelections((prev) => {
      const updated = { ...prev };
      delete updated[tailNumberToRemove];
      return updated;
    });
  };

  // Check if all unfound tail numbers have been handled (either selected aircraft or removed)
  const allUnfoundHandled =
    unfoundTailNumbers.length === 0 ||
    unfoundTailNumbers.every((tn) => manualAircraftSelections[tn]);

  // Show submit button only if we have searched and all unfound are handled
  const showSubmitButton =
    hasSearched && tailNumbers.length > 0 && allUnfoundHandled;
  return (
    <Stack direction="column" spacing={1} width="100%">
      {!hasSubmitted ? (
        <>
          <TextField
            placeholder={`N12345
N54321
N67890`}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={10}
            onChange={(e) => {
              const value = e.target.value
                .split("\n")
                .map((t) => t.trim())
                .filter((t) => t.length);
              setTailNumbers(value);
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Enter one tail number per line.
          </Typography>

          {!hasSearched && (
            <Button
              variant="contained"
              color="primary"
              disabled={tailNumbers.length === 0}
              onClick={handleSubmit}
            >
              Search
            </Button>
          )}

          {hasSearched &&
            unfoundTailNumbers.length === 0 &&
            tailNumbers.length > 0 && (
              <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                ✓ All tail numbers found in database
              </Typography>
            )}

          {hasSearched && unfoundTailNumbers.length > 0 && (
            <>
              <Typography
                variant="subtitle2"
                color="warning.main"
                sx={{ mt: 2 }}
              >
                Unknown Tail Numbers
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The following tail numbers were not found in the database.
                Please select the aircraft type for each or remove them:
              </Typography>
              <Stack direction="column" spacing={2} sx={{ mt: 1 }}>
                {unfoundTailNumbers.map((tailNumber) => (
                  <Stack
                    key={tailNumber}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                  >
                    <Typography
                      variant="body2"
                      sx={{ minWidth: 80, fontWeight: "bold" }}
                    >
                      {tailNumber}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }}>
                      <AutocompleteAircraft
                        label="Select aircraft type"
                        onSelect={(aircraft) => {
                          setManualAircraftSelections((prev) => ({
                            ...prev,
                            [tailNumber]: aircraft,
                          }));
                        }}
                        defaultSelected={
                          manualAircraftSelections[tailNumber] || null
                        }
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => removeUnfoundTailNumber(tailNumber)}
                    >
                      Remove
                    </Button>
                  </Stack>
                ))}
              </Stack>
            </>
          )}

          {showSubmitButton && (
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              sx={{ mt: 2 }}
            >
              Add Aircraft
            </Button>
          )}
        </>
      ) : (
        <Stack
          direction="column"
          spacing={2}
          alignItems="center"
          sx={{ py: 2 }}
        >
          <Typography
            variant="body2"
            color="success.main"
            sx={{ textAlign: "center" }}
          >
            ✓ Aircraft have been added to the list below
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setHasSubmitted(false);
              setHasSearched(false);
              setTailNumbers([]);
              setUnfoundTailNumbers([]);
              setManualAircraftSelections({});
            }}
          >
            Add More Tail Numbers
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

type Props = {
  stack: StackType | null;
  bulkTenantsToAdd: Tenant[];
  setBulkTenantsToAdd: React.Dispatch<React.SetStateAction<Tenant[]>>;
};

export const BulkAdd: React.FC<Props> = ({
  stack,
  bulkTenantsToAdd,
  setBulkTenantsToAdd,
}) => {
  const [activeTab, setActiveTab] = React.useState<string>(
    "Add by Make and Model"
  );
  const { airplxIdentity } = useIdentity();
  const { activeFBO } = useActiveFBOs();
  const { tenants } = useTenants();
  const snackbar = useSnackbar();
  const [newAircraft, setNewAircraft] = React.useState<Aircraft | null>(null);
  const [nAircraft, setNAircraft] = React.useState<number>(1);
  const [tailNumbers, setTailNumbers] = React.useState<string>("");

  return (
    <Stack direction="column" spacing={2} width="100%">
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => {
          setBulkTenantsToAdd([]); // reset the tenants to add when switching tabs
          setActiveTab(newValue);
        }}
        variant="fullWidth"
        sx={{ width: "100%", mb: 2 }}
      >
        <Tab value="Add by Make and Model" label="Add by Make and Model" />
        <Tab value="Add by Tail Numbers" label="Add by Tail Numbers" />
      </Tabs>
      {activeTab === "Add by Tail Numbers" && (
        <BulkAddByTailNumber
          stack={stack}
          bulkTenantsToAdd={bulkTenantsToAdd}
          setBulkTenantsToAdd={setBulkTenantsToAdd}
        />
      )}
      {activeTab === "Add by Make and Model" && (
        <Stack direction="row" spacing={2} alignItems="flex-start" width="100%">
          <Box width={350} flexShrink={0}>
            <AutocompleteAircraft
              label="Search by make and model"
              onSelect={(aircraft) => setNewAircraft(aircraft)}
              defaultSelected={newAircraft}
            />
          </Box>
          {/* number of aircraft to add */}
          <TextField
            label="# Aircraft"
            type="number"
            variant="outlined"
            size="small"
            inputProps={{ min: 1, max: 20 }}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (value > 20) {
                snackbar.notify({
                  severity: "warning",
                  text: "Maximum number of aircraft to add is 20.",
                  duration: 5000,
                });
              }
              setNAircraft(Math.min(20, value));
            }}
            value={nAircraft}
            sx={{
              width: 100,
            }}
          />
          {/* textfield for plugging in a comma delimited list of tail numbers */}
          <Box flexGrow={1}>
            <TextField
              label="Tail Numbers"
              placeholder="i.e. N12345, N54321, N67890"
              variant="outlined"
              size="small"
              fullWidth
              onChange={(e) => {
                // remove all commas or non-alphanumeric characters
                const value = e.target.value.replace(/[^a-zA-Z0-9,]/g, "");
                setTailNumbers(value);
              }}
              value={tailNumbers}
            />
            <Typography variant="caption" color="text.secondary">
              Optional. Separate tail numbers with commas.
            </Typography>
          </Box>
          <Box flexGrow={1}>
            <Button
              disabled={!newAircraft || nAircraft === 0}
              variant="contained"
              onClick={() => {
                const newTenants = Array.from({ length: nAircraft }, (_, i) => {
                  // extract tail_number from comma delimited list. if it is "" then set it to null
                  const tail_number = tailNumbers
                    .split(",")
                    .map((t) => t.trim())
                    .filter((t) => t.length)[i];
                  const existingTenant = tenants.find(
                    (t) => t.tail_number === tail_number
                  );

                  if (tail_number?.length && existingTenant) {
                    // if the aircraft is different, tell the user
                    if (
                      existingTenant.aircraft.make !== newAircraft.make ||
                      existingTenant.aircraft.model !== newAircraft.model
                    ) {
                      snackbar.notify({
                        severity: "warning",
                        text: `Aircraft with tail number ${tail_number} already exists. We’ve added the existing tail number and associated aircraft model.`,
                        duration: 5000,
                      });
                    }
                    return {
                      ...existingTenant,
                      selected: false,
                      position: {
                        ...existingTenant.position,
                        stack_id: stack?.id ?? null,
                        // reset the position
                        x: null,
                        y: stack
                          ? -1 *
                            Math.max(newAircraft.length, newAircraft.wingspan)
                          : null,
                        angle: null,
                        preferences: defaultPreferenceForAircraft(
                          newAircraft,
                          activeFBO
                        ),
                      },
                    };
                  }
                  const entity_id = uuidv4();
                  const tenant: Tenant = {
                    id: uuidv4(),
                    placement_id: uuidv4(),
                    fbo_id: activeFBO.id,
                    entity_id,
                    tail_number: tail_number || "",
                    owner_manager: "",
                    note: "",
                    note_last_updated: new Date().toISOString(),
                    note_last_updated_by: airplxIdentity?.email ?? "Unknown",
                    type: stack?.isReference ? "base" : "transient",
                    aircraft_id: newAircraft.id,
                    aircraft: newAircraft,
                    selected: false,
                    position: {
                      id: consistentId(entity_id, false),
                      stack_id: stack?.id ?? null,
                      entity_id,
                      x: null,
                      y: stack
                        ? -1 * Math.max(newAircraft.length, newAircraft.wingspan)
                        : null,
                      angle: null,
                      preferences: defaultPreferenceForAircraft(
                        newAircraft,
                        activeFBO
                      ),
                    },
                  };
                  return tenant;
                });
                const newTenantsToAdd = uniqBy(
                  [...bulkTenantsToAdd, ...newTenants],
                  "id"
                );
                setBulkTenantsToAdd(newTenantsToAdd);
                // reset
                setNewAircraft(null);
                setNAircraft(1);
                setTailNumbers("");
              }}
              startIcon={<AddIcon />}
            >
              Add
            </Button>
          </Box>
        </Stack>
      )}
      {bulkTenantsToAdd.length > 0 && (
        <TableContainer sx={{ maxWidth: 640 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tail #</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Type</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bulkTenantsToAdd.map((tenant) => (
                <TableRow key={`tenant-to-add-${tenant.id}`}>
                  <TableCell>{tenant.tail_number ?? "N/A"}</TableCell>
                  <TableCell>{tenant.aircraft.model}</TableCell>
                  <TableCell>{tenant.type}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() =>
                        setBulkTenantsToAdd((prev) =>
                          prev.filter((t) => t.id !== tenant.id)
                        )
                      }
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};
